/**
 * LotSmith — Rules panel.
 *
 * Per-firm rule progress with horizontal bars. Renders next to the
 * Equity curve on the dashboard (per plan §05-mockups/03 web dashboard
 * mockup). Each row is { label · bar · right-side hint }.
 *
 * Bars driven by metrics already computed server-side in
 * worker-license/src/reads.ts:
 *   - Daily loss      ← metrics.dailyLossUsedPct
 *   - Profit target   ← metrics.targetProgressPct
 *   - Max drawdown    ← metrics.totalLossUsedPct
 *   - Trailing DD     ← metrics.trailingDrawdownUsedPct
 *   - Min days        ← profitable day count / configured requirement
 *   - Consistency     ← best profitable day / total closed profit
 */
import type { DevMetrics } from "../providers/dataContext.js";

export interface RulesPanelProps {
  metrics: DevMetrics;
  firmLabel: string;
}

interface RuleRow {
  label: string;
  pct: number;            // 0..1
  rightLabel: string;
  tone: "warning" | "profit" | "info" | "muted";
}

export function RulesPanel({ metrics, firmLabel }: RulesPanelProps) {
  const rules = metrics.rules;
  if (!rules) {
    return (
      <section className="panel rules-panel">
        <div className="panel__hdr">
          <h2 className="panel__title">Rules</h2>
        </div>
        <div className="panel__body panel__body--muted">
          Rule context unavailable.
        </div>
      </section>
    );
  }

  const dailyUsed = clamp(metrics.dailyLossUsedPct);
  const dailyTone = dailyUsed >= 0.8 ? "warning" : dailyUsed >= 0.5 ? "warning" : "muted";
  const totalUsed = clamp(metrics.totalLossUsedPct);
  const totalTone = totalUsed >= 0.8 ? "warning" : totalUsed >= 0.5 ? "warning" : "muted";
  const trailingUsed = clamp(metrics.trailingDrawdownUsedPct);
  const trailingTone = trailingUsed >= 0.8 ? "warning" : trailingUsed >= 0.5 ? "warning" : "muted";
  const targetReached = clamp(metrics.targetProgressPct);
  const minDaysRequired = metrics.minTradingDaysRequired || rules.minTradingDays || 0;
  const minDaysPct = clamp(metrics.minTradingDaysProgressPct);
  const consistencyLimit = metrics.consistencyLimitPct || rules.consistencyMaxDayPct || 0;
  const consistencyUsed = clamp(metrics.consistencyUsedPct);
  const consistencyTone = consistencyLimit > 0 && consistencyUsed > consistencyLimit ? "warning" : "muted";

  const rows: RuleRow[] = [
    {
      label: "Daily loss",
      pct: dailyUsed,
      rightLabel: `${Math.round(dailyUsed * 100)}% used`,
      tone: dailyTone,
    },
    {
      label: "Max drawdown",
      pct: totalUsed,
      rightLabel: `${Math.round(totalUsed * 100)}% used`,
      tone: totalTone,
    },
    {
      label: "Trailing DD",
      pct: trailingUsed,
      rightLabel: `${Math.round(trailingUsed * 100)}% from HWM`,
      tone: trailingTone,
    },
    {
      label: "Profit target",
      pct: targetReached,
      rightLabel: rules.targetPct > 0 ? `${Math.round(targetReached * 100)}% reached` : "no target (FUNDED)",
      tone: targetReached >= 1 ? "profit" : "info",
    },
    {
      label: "Min trading days",
      pct: minDaysRequired > 0 ? minDaysPct : 0,
      rightLabel: minDaysRequired > 0
        ? `${metrics.minTradingDaysDone} / ${minDaysRequired}`
        : "not required",
      tone: minDaysRequired > 0
        ? (minDaysPct < 1 ? "info" : "profit")
        : "muted",
    },
    {
      label: "Consistency",
      pct: consistencyLimit > 0 ? consistencyUsed : 0,
      rightLabel: consistencyLimit > 0
        ? `${Math.round(consistencyUsed * 100)}% / ${Math.round(consistencyLimit * 100)}%`
        : "not required",
      tone: consistencyLimit > 0 ? consistencyTone : "muted",
    },
  ];

  return (
    <section className="panel rules-panel">
      <div className="panel__hdr">
        <h2 className="panel__title">{firmLabel} rules</h2>
      </div>
      <div className="rules-panel__body">
        {rows.map((r) => <RuleBar key={r.label} row={r} />)}
      </div>
    </section>
  );
}

function RuleBar({ row }: { row: RuleRow }) {
  const widthPct = Math.round(row.pct * 100);
  return (
    <div className="rules-panel__row">
      <div className="rules-panel__label">{row.label}</div>
      <div className="rules-panel__track">
        <div
          className={`rules-panel__fill rules-panel__fill--${row.tone}`}
          style={{ width: `${Math.max(2, widthPct)}%` }}
        />
      </div>
      <div className={`rules-panel__right tone-${row.tone}`}>{row.rightLabel}</div>
    </div>
  );
}

function clamp(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
